import { useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '@/features/auth';
import {
  dismissNotification,
  listNotifications,
  markAllNotificationsSeen,
  markNotificationSeen,
  refreshInvoicingWarnings,
  type NotificationRecord,
  type NotificationsResponse,
} from '@/features/planning/api';
import type { Language } from '@/types';

interface Props {
  language: Language;
}

type Severity = NotificationRecord['severity'];

const SEV_DOT: Record<Severity, string> = {
  info: '#607C6E',
  warning: '#B97E1F',
  error: '#C24138',
};

const SEV_TAG_BG: Record<Severity, string> = {
  info: 'rgba(96,124,110,0.10)',
  warning: 'rgba(185,126,31,0.10)',
  error: 'rgba(194,65,56,0.10)',
};

const SEV_LABEL: Record<Severity, string> = {
  info: 'INFO',
  warning: 'VARN',
  error: 'FEL',
};

function formatStamp(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'NYSS';
  if (min < 60) return `${min}M`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}H`;
  const d = Math.floor(h / 24);
  return `${d}D`;
}

export function NotificationsWorkspace({ language }: Props) {
  const { session, profile } = useAuth();
  const token = session?.access_token ?? null;
  const isAdmin = profile?.role === 'admin';
  const sv = language === 'sv';

  const [data, setData] = useState<NotificationsResponse | null>(null);
  const [includeDismissed, setIncludeDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await listNotifications(token, includeDismissed);
      setData(res);
    } catch (err) {
      console.error('Failed to load notifications', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, includeDismissed]);

  const handleRefreshWarnings = async () => {
    if (!token) return;
    setWorking(true);
    try {
      await refreshInvoicingWarnings({}, token);
      await load();
    } finally {
      setWorking(false);
    }
  };

  const handleMarkAllSeen = async () => {
    if (!token || !data) return;
    setWorking(true);
    // Optimistic update
    const prev = data;
    const now = new Date().toISOString();
    setData({
      ...data,
      counts: { ...data.counts, unseen: 0 },
      notifications: data.notifications.map((n) =>
        n.seen_at ? n : { ...n, seen_at: now }
      ),
    });
    try {
      await markAllNotificationsSeen(token);
    } catch (err) {
      console.error('Mark all seen failed', err);
      setData(prev);
      await load();
    } finally {
      setWorking(false);
    }
  };

  const handleDismiss = async (n: NotificationRecord) => {
    if (!token || !data) return;
    // Optimistic update
    const prev = data;
    const now = new Date().toISOString();
    if (includeDismissed) {
      setData({
        ...data,
        notifications: data.notifications.map((item) =>
          item.id === n.id ? { ...item, dismissed_at: now } : item
        ),
      });
    } else {
      setData({
        ...data,
        notifications: data.notifications.filter((item) => item.id !== n.id),
      });
    }
    try {
      await dismissNotification(n.id, token);
    } catch (err) {
      console.error('Dismiss failed', err);
      setData(prev);
    }
  };

  const handleSeen = async (n: NotificationRecord) => {
    if (!token || !data || n.seen_at) return;
    // Optimistic update
    const prev = data;
    const now = new Date().toISOString();
    setData({
      ...data,
      counts: { ...data.counts, unseen: Math.max(0, data.counts.unseen - 1) },
      notifications: data.notifications.map((item) =>
        item.id === n.id ? { ...item, seen_at: now } : item
      ),
    });
    try {
      await markNotificationSeen(n.id, token);
    } catch {
      setData(prev);
    }
  };

  const count = data?.notifications.length ?? 0;

  return (
    <div className="cmd-canvas min-h-full p-4 sm:p-6">
      <div className="cmd-panel cmd-panel-inner rounded p-4 max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2.5">
            <span className="cmd-label">{sv ? 'NOTIFIERINGAR — LOGG' : 'NOTIFICATIONS — LOG'}</span>
            {data && (
              <span className="cmd-label text-[#8A6A47] tabular-nums">
                {count.toString().padStart(2, '0')}
              </span>
            )}
          </div>

          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setIncludeDismissed((prev) => !prev)}
              className="cmd-label text-[#8A6A47] hover:text-[#6E5438] transition-colors text-[9px]"
            >
              {includeDismissed
                ? sv ? 'GÖM STÄNGDA' : 'HIDE DISMISSED'
                : sv ? 'VISA STÄNGDA' : 'SHOW DISMISSED'}
            </button>
            {isAdmin && (
              <button
                type="button"
                onClick={handleRefreshWarnings}
                disabled={working}
                className="cmd-label text-[#8A6A47] hover:text-[#6E5438] transition-colors text-[9px] flex items-center gap-1 disabled:opacity-50"
              >
                {working ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <RefreshCw className="h-3 w-3" />
                )}
                {sv ? 'SÖK NYA' : 'SCAN'}
              </button>
            )}
            <button
              type="button"
              onClick={handleMarkAllSeen}
              disabled={working}
              className="cmd-label text-[#8A6A47] hover:text-[#6E5438] transition-colors text-[9px] disabled:opacity-50"
            >
              {sv ? 'MARKERA ALLA LÄSTA' : 'MARK ALL READ'}
            </button>
          </div>
        </div>

        {/* Count strip */}
        {data && (
          <div className="flex items-center gap-5 mb-4 pb-3 border-b border-[#073331]/10">
            <CountStat label={sv ? 'OLÄSTA' : 'UNREAD'} value={data.counts.unseen} tone="#073331" />
            <CountStat label={sv ? 'VARN' : 'WARN'} value={data.counts.warning} tone="#B97E1F" />
            <CountStat label={sv ? 'FEL' : 'ERR'} value={data.counts.error} tone="#C24138" />
          </div>
        )}

        {/* Feed */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-[#607C6E]" />
          </div>
        ) : count === 0 ? (
          <div className="flex items-center gap-2 py-3">
            <div
              className="w-1.5 h-1.5 rounded-full shrink-0 cmd-pulse"
              style={{ background: '#1B7F4D' }}
            />
            <span className="text-xs font-mono text-[#1B7F4D]">
              {sv ? 'INGA NOTIFIERINGAR' : 'NO NOTIFICATIONS'}
            </span>
          </div>
        ) : (
          <div className="space-y-0.5">
            {data!.notifications.map((n) => {
              const isDismissed = Boolean(n.dismissed_at);
              const isNew = !n.seen_at && !isDismissed;
              return (
                <div
                  key={n.id}
                  className={cnRow(isDismissed)}
                >
                  {/* Severity dot */}
                  <div
                    className="w-1.5 h-1.5 rounded-full shrink-0 mt-[7px]"
                    style={{ background: SEV_DOT[n.severity] }}
                  />

                  {/* Tag chip */}
                  <span
                    className="cmd-label shrink-0 px-1.5 py-0.5 rounded text-[9px] mt-0.5"
                    style={{
                      background: SEV_TAG_BG[n.severity],
                      color: SEV_DOT[n.severity],
                    }}
                  >
                    {SEV_LABEL[n.severity]}
                  </span>

                  {/* Title + body (mono) */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-[#073331] leading-tight">
                        {n.title}
                      </span>
                      {isNew && (
                        <span className="cmd-label text-[8px] text-[#8A6A47]">{sv ? 'NYTT' : 'NEW'}</span>
                      )}
                    </div>
                    {n.body && (
                      <p className="text-xs font-mono text-[#607C6E] mt-0.5 leading-snug">{n.body}</p>
                    )}
                  </div>

                  {/* Timestamp + actions */}
                  <div className="flex items-center gap-3 shrink-0 mt-0.5">
                    <span className="cmd-label text-[9px] tabular-nums">{formatStamp(n.created_at)}</span>
                    {isNew && (
                      <button
                        type="button"
                        onClick={() => void handleSeen(n)}
                        className="cmd-label text-[#8A6A47] hover:text-[#6E5438] transition-colors text-[9px]"
                      >
                        {sv ? 'LÄST' : 'SEEN'}
                      </button>
                    )}
                    {!isDismissed && (
                      <button
                        type="button"
                        onClick={() => void handleDismiss(n)}
                        className="cmd-label text-[#8A6A47] hover:text-[#6E5438] transition-colors text-[9px]"
                      >
                        {sv ? 'STÄNG' : 'DISMISS'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function cnRow(isDismissed: boolean): string {
  const base =
    'flex items-start gap-2.5 px-2 py-2 rounded transition-colors border-b border-[#073331]/[0.06] last:border-0 hover:bg-[#073331]/[0.04]';
  return isDismissed ? `${base} opacity-50` : base;
}

function CountStat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-base font-mono tabular-nums font-semibold leading-none" style={{ color: tone }}>
        {value}
      </span>
      <span className="cmd-label text-[9px]">{label}</span>
    </div>
  );
}
